import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext, useAsyncData } from '@/hooks';
import { api, ApiError } from '@/api/client';
import { formatRelative } from '@/utils/format';
import type { ApprovalRequest } from '@/types';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import solutionMindset from '@/assets/images/undraw_solution-mindset_5xp7.svg';
import { Modal } from '@/components/ui/Modal';
import { FormField } from '@/components/ui/FormField';
import { useToast } from '@/components/ui/Toast';
import { Icon } from '@/components/icons';
import styles from './ApprovalsPage.module.css';

const TYPE_KEY: Record<ApprovalRequest['type'], string> = {
  ACADEMIC_YEAR: 'approvals.typeAcademicYear',
  PROFILE_CHANGE: 'approvals.typeProfileChange',
};

const APPROVER_KEY: Record<string, string> = {
  DEAN: 'approvals.approverDean',
  PRINCIPAL: 'approvals.approverPrincipal',
  ACADEMIC_DIRECTOR: 'approvals.approverAcademicDirector',
};

const APPROVER_ROLE_CODES = new Set(['DEAN', 'PRINCIPAL', 'ACADEMIC_DIRECTOR']);

function isSelfSubmission(
  request: ApprovalRequest,
  identityId: string | undefined,
): boolean {
  if (!identityId || !request.requestedBySubject) return false;
  return request.requestedBySubject === identityId;
}

/** Only the role the request is addressed to may decide — and never the submitter. */
function canDecideRequest(
  request: ApprovalRequest,
  contextRoleCode: string | undefined,
  identityId: string | undefined,
): boolean {
  if (!contextRoleCode || contextRoleCode !== request.approverRole) return false;
  return !isSelfSubmission(request, identityId);
}

export function ApprovalsPage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { profile, activeContext } = useAppContext();
  const institutionId = activeContext?.institution?.id ?? '';
  const contextRoleCode = activeContext?.role?.code;
  const identityId = profile?.identityId;

  const { state, reload, replace } = useAsyncData<ApprovalRequest[]>(
    `admin-approvals-${institutionId}`,
    () => api.admin.approvals.list(institutionId),
    !!institutionId,
  );

  const [decision, setDecision] = useState<{
    request: ApprovalRequest | null;
    action: 'approve' | 'reject';
  }>({ request: null, action: 'approve' });
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const requests = state.status === 'ready' ? state.data : [];
  const pending = useMemo(() => requests.filter((r) => r.status === 'PENDING'), [requests]);
  const decided = useMemo(() => requests.filter((r) => r.status !== 'PENDING'), [requests]);

  const pageSubtitle = APPROVER_ROLE_CODES.has(contextRoleCode ?? '')
    ? t('approvals.subtitleApprover')
    : t('approvals.subtitleViewer');

  const approverLabel = (role: string) =>
    APPROVER_KEY[role] ? t(APPROVER_KEY[role]) : role;

  const viewerNote = (request: ApprovalRequest): string => {
    const role = approverLabel(request.approverRole);
    return isSelfSubmission(request, identityId)
      ? t('approvals.submittedAwaiting', { role })
      : t('approvals.awaitingDecision', { role });
  };

  const openDecision = (request: ApprovalRequest, action: 'approve' | 'reject') => {
    setNote('');
    setDecision({ request, action });
  };

  const closeDecision = () => setDecision({ request: null, action: 'approve' });

  const handleDecide = async () => {
    if (!institutionId || !decision.request) return;
    setSubmitting(true);
    try {
      const updated =
        decision.action === 'approve'
          ? await api.admin.approvals.approve(institutionId, decision.request.id, note || undefined)
          : await api.admin.approvals.reject(institutionId, decision.request.id, note || undefined);
      replace(requests.map((r) => (r.id === updated.id ? updated : r)));
      closeDecision();
      toast.success(
        decision.action === 'approve'
          ? t('approvals.approvedToast', { label: updated.targetLabel })
          : t('approvals.rejectedToast', { label: updated.targetLabel }),
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        const role = decision.request
          ? approverLabel(decision.request.approverRole)
          : t('approvals.approverPrincipal');
        toast.error(
          isSelfSubmission(decision.request!, identityId)
            ? t('approvals.selfApprovalBlocked', { role })
            : t('approvals.notApprover', { role }),
        );
      } else {
        toast.error(t('common.actionFailed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const payloadSummary = (request: ApprovalRequest): string | null => {
    if (request.type === 'ACADEMIC_YEAR') {
      const start = request.payload?.startDate;
      const end = request.payload?.endDate;
      return start && end ? `${String(start)} → ${String(end)}` : null;
    }
    const reason = request.payload?.reason;
    return reason ? String(reason) : null;
  };

  const requestedChanges = (request: ApprovalRequest): [string, string][] => {
    if (request.type !== 'PROFILE_CHANGE') return [];
    const changes = request.payload?.changes;
    if (!changes || typeof changes !== 'object') return [];
    return Object.entries(changes as Record<string, unknown>).map(([k, v]) => [k, String(v)]);
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.headerName}>{t('approvals.title')}</h1>
          <p className={styles.headerSubtitle}>{pageSubtitle}</p>
        </div>
      </header>

      {state.status === 'loading' && (
        <div className={styles.loading}>{t('common.loading')}</div>
      )}

      {state.status === 'error' && (
        <Card className={styles.emptyState}>
          <Icon name="error" size={48} />
          <h2 className={styles.emptyStateTitle}>{t('approvals.title')}</h2>
          <p className={styles.emptyStateBody}>{t('common.error')}</p>
          <button className={styles.retryButton} onClick={reload}>{t('common.retry')}</button>
        </Card>
      )}

      {state.status === 'ready' && (
        <>
          {pending.length === 0 ? (
            <Card>
              <EmptyState
                illustration={solutionMindset}
                title={t('approvals.allClear')}
                description={t('approvals.allClearBody')}
              />
            </Card>
          ) : (
            <div className={styles.pendingList}>
              {pending.map((request) => {
                const mayDecide = canDecideRequest(request, contextRoleCode, identityId);
                return (
                  <Card key={request.id} className={styles.pendingCard}>
                    <div className={styles.pendingMain}>
                      <div className={styles.pendingMeta}>
                        <span className={styles.typeChip}>{t(TYPE_KEY[request.type])}</span>
                        <span className={styles.approverChip}>
                          {t('approvals.awaits', { role: approverLabel(request.approverRole) })}
                        </span>
                      </div>
                      <h3 className={styles.pendingLabel}>{request.targetLabel}</h3>
                      {payloadSummary(request) && (
                        <p className={styles.pendingSummary}>{payloadSummary(request)}</p>
                      )}
                      {requestedChanges(request).length > 0 && (
                        <dl className={styles.changesList}>
                          {requestedChanges(request).map(([field, value]) => (
                            <div key={field} className={styles.changesRow}>
                              <dt>{field}</dt>
                              <dd>{value}</dd>
                            </div>
                          ))}
                        </dl>
                      )}
                      <p className={styles.pendingBy}>
                        {t('approvals.requestedBy', {
                          name: request.requestedBy,
                          when: formatRelative(request.requestedAt, i18n.language),
                        })}
                      </p>
                    </div>
                    <div className={styles.pendingActions}>
                      {mayDecide ? (
                        <>
                          <Button size="sm" onClick={() => openDecision(request, 'approve')}>
                            {t('approvals.approve')}
                          </Button>
                          <Button size="sm" variant="danger" onClick={() => openDecision(request, 'reject')}>
                            {t('approvals.reject')}
                          </Button>
                        </>
                      ) : (
                        <p className={styles.pendingViewerNote}>{viewerNote(request)}</p>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}

          {decided.length > 0 && (
            <section className={styles.historySection}>
              <h2 className={styles.historyTitle}>{t('approvals.history')}</h2>
              <Card>
                <div className="table-scroll">
                  <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>{t('approvals.request')}</th>
                      <th>{t('approvals.decision')}</th>
                      <th>{t('approvals.decidedBy')}</th>
                      <th>{t('approvals.note')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {decided.map((request) => (
                      <tr key={request.id}>
                        <td>
                          <div className={styles.historyLabel}>{request.targetLabel}</div>
                          <div className={styles.historySub}>{t(TYPE_KEY[request.type])}</div>
                        </td>
                        <td>
                          <span
                            className={`${styles.statusBadge} ${request.status === 'APPROVED' ? styles.statusApproved : styles.statusRejected}`}
                          >
                            {request.status === 'APPROVED'
                              ? t('approvals.approved')
                              : t('approvals.rejected')}
                          </span>
                        </td>
                        <td>
                          <div>{request.decidedBy ?? '—'}</div>
                          <div className={styles.historySub}>
                            {request.decidedAt ? formatRelative(request.decidedAt, i18n.language) : ''}
                          </div>
                        </td>
                        <td className={styles.noteCell}>{request.decisionNote ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                  </table>
                </div>
              </Card>
            </section>
          )}
        </>
      )}

      <Modal
        open={!!decision.request}
        onClose={closeDecision}
        title={
          decision.action === 'approve'
            ? t('approvals.approveDialogTitle')
            : t('approvals.rejectDialogTitle')
        }
        description={decision.request?.targetLabel}
        closeOnBackdropClick={false}
      >
        <div className={styles.dialogForm}>
          <FormField
            id="decision-note"
            label={t('approvals.noteLabel')}
            hint={
              decision.action === 'reject'
                ? t('approvals.rejectNoteHint')
                : undefined
            }
          >
            <textarea
              id="decision-note"
              className={styles.noteInput}
              rows={3}
              placeholder={t('approvals.notePlaceholder')}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </FormField>
          <div className={styles.dialogActions}>
            <Button variant="secondary" onClick={closeDecision}>{t('common.cancel')}</Button>
            <Button
              variant={decision.action === 'reject' ? 'danger' : 'primary'}
              onClick={handleDecide}
              loading={submitting}
              disabled={submitting}
            >
              {decision.action === 'approve'
                ? t('approvals.approve')
                : t('approvals.reject')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
